import type { IOpeningHourRow } from '@/interfaces/IStoreSetup';
import {
  summarizeWeekdays,
  weekdaySummaryLabel,
} from '@/domain/reservations/reservation-guide';
import type { IWeekdayLabelCopy } from '@/domain/reservations/reservation-guide';

/**
 * Opening hours of the setup flow. The API stores `hour-ranges` rows
 * (`IOpeningHourRow`, day 0 = Sunday, `to` '23:59' = end of day, overnight
 * ranges split at midnight); the owner edits a week of ranges where a range
 * whose end is not later than its start ends the next day.
 */

export interface ITimeRange {
  from: string;
  to: string;
}

export interface IDayHours {
  open: boolean;
  ranges: ITimeRange[];
}

/** Seven days, index 0 = Sunday (the API's day numbers). */
export type TWeekHours = IDayHours[];

export const START_OF_DAY = '00:00';
export const END_OF_DAY = '23:59';
/** Ranges the hours editor allows per day. */
export const MAX_RANGES_PER_DAY = 3;
/** Stored rows the API allows per day (after overnight splits). */
export const MAX_ROWS_PER_DAY = 4;

const DAYS_PER_WEEK = 7;
/** Monday-first order of the API's Sunday = 0 weekday numbers. */
const WEEKDAYS_MON_FIRST: readonly number[] = [1, 2, 3, 4, 5, 6, 0];
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

function byFrom(a: { from: string }, b: { from: string }): number {
  if (a.from === b.from) return 0;
  return a.from < b.from ? -1 : 1;
}

function isWeekday(day: number): boolean {
  return Number.isInteger(day) && day >= 0 && day < DAYS_PER_WEEK;
}

function closedDay(): IDayHours {
  return { open: false, ranges: [] };
}

/** Rows per weekday (index 0 = Sunday); rows of any other day are left out. */
function groupByDay(rows: IOpeningHourRow[]): IOpeningHourRow[][] {
  const byDay: IOpeningHourRow[][] = Array.from(
    { length: DAYS_PER_WEEK },
    () => []
  );
  for (const row of rows) if (isWeekday(row.day)) byDay[row.day].push(row);
  return byDay;
}

/**
 * Stored rows → editable week. A `X–23:59` row on day N and a `00:00–Y` row on
 * day N+1 (with Y not later than X) become one overnight range `X–Y` on day N;
 * a `23:59` end alone reads as `00:00`.
 */
export function rowsToWeek(rows: IOpeningHourRow[]): TWeekHours {
  const byDay = groupByDay(rows);
  for (const dayRows of byDay) dayRows.sort(byFrom);

  const continuations = new Map<IOpeningHourRow, string>();
  const consumed = new Set<IOpeningHourRow>();
  byDay.forEach((dayRows, day) => {
    const nextDayRows = byDay[(day + 1) % DAYS_PER_WEEK];
    for (const row of dayRows) {
      if (row.to !== END_OF_DAY) continue;
      const next = nextDayRows.find(
        (candidate) =>
          candidate.from === START_OF_DAY &&
          candidate.to <= row.from &&
          !consumed.has(candidate)
      );
      if (!next) continue;
      consumed.add(next);
      continuations.set(row, next.to);
    }
  });

  return byDay.map((dayRows) => {
    const ranges = dayRows
      .filter((row) => !consumed.has(row))
      .map((row) => {
        const continuation = continuations.get(row);
        if (continuation !== undefined)
          return { from: row.from, to: continuation };
        return {
          from: row.from,
          to: row.to === END_OF_DAY ? START_OF_DAY : row.to,
        };
      });
    return { open: ranges.length > 0, ranges };
  });
}

/**
 * Editable week → stored rows. A range whose `to ≤ from` splits into
 * `(day, from, '23:59')` + `(day + 1, '00:00', to)`, the second skipped when
 * `to` is '00:00'. Closed days give no rows.
 */
export function weekToRows(week: TWeekHours): IOpeningHourRow[] {
  const rows: IOpeningHourRow[] = [];
  week.slice(0, DAYS_PER_WEEK).forEach((dayHours, day) => {
    if (!dayHours.open) return;
    for (const range of dayHours.ranges) {
      if (range.to > range.from) {
        rows.push({ day, from: range.from, to: range.to });
        continue;
      }
      rows.push({ day, from: range.from, to: END_OF_DAY });
      if (range.to !== START_OF_DAY)
        rows.push({
          day: (day + 1) % DAYS_PER_WEEK,
          from: START_OF_DAY,
          to: range.to,
        });
    }
  });
  return rows.sort((a, b) => a.day - b.day || byFrom(a, b));
}

/**
 * The API's `validateHourRows` (§6.4), line for line: day 0..6; `from < to`;
 * at most 4 rows per day; sorted by `from`, each row starts at or after the
 * previous row's `to`.
 */
export function validateHourRows(rows: IOpeningHourRow[]): boolean {
  for (const row of rows) {
    if (!isWeekday(row.day)) return false;
    if (!(row.from < row.to)) return false;
  }
  for (const dayRows of groupByDay(rows)) {
    if (dayRows.length > MAX_ROWS_PER_DAY) return false;
    const sorted = [...dayRows].sort(byFrom);
    for (let i = 1; i < sorted.length; i++)
      if (sorted[i].from < sorted[i - 1].to) return false;
  }
  return true;
}

/**
 * What the hours editor accepts: seven days, every open day with 1 to 3
 * complete ranges, and rows the API accepts (so an overnight Friday range plus
 * a Saturday 01:00 range is caught here, never answered HOURS_INVALID).
 */
export function validateWeek(week: TWeekHours): boolean {
  if (week.length !== DAYS_PER_WEEK) return false;
  for (const dayHours of week) {
    if (!dayHours.open) continue;
    if (
      dayHours.ranges.length === 0 ||
      dayHours.ranges.length > MAX_RANGES_PER_DAY
    )
      return false;
    if (
      dayHours.ranges.some(
        (range) => !TIME.test(range.from) || !TIME.test(range.to)
      )
    )
      return false;
  }
  return validateHourRows(weekToRows(week));
}

/** «12:00–15:30 · 19:30–00:00», or null when the day is closed. */
function dayHoursLabel(dayHours: IDayHours | undefined): string | null {
  if (!dayHours || !dayHours.open || dayHours.ranges.length === 0) return null;
  return [...dayHours.ranges]
    .sort(byFrom)
    .map((range) => `${range.from}–${range.to}`)
    .join(' · ');
}

export interface IHoursDisplayCopy extends IWeekdayLabelCopy {
  closed: string;
}

export interface IHoursDisplayRow {
  days: string;
  hours: string;
}

/**
 * One row per run of consecutive days (Monday first) with identical hours:
 * «Lun a Jue · 12:00–15:30 · 19:30–00:00», «Vie y Sáb», «Todos los días»;
 * closed days read `copy.closed`.
 */
export function hoursDisplayRows(
  week: TWeekHours,
  locale: string,
  copy: IHoursDisplayCopy
): IHoursDisplayRow[] {
  const groups: { days: number[]; hours: string | null }[] = [];
  for (const day of WEEKDAYS_MON_FIRST) {
    const hours = dayHoursLabel(week[day]);
    const last = groups[groups.length - 1];
    if (last && last.hours === hours) last.days.push(day);
    else groups.push({ days: [day], hours });
  }
  return groups.map((group) => ({
    days: weekdaySummaryLabel(summarizeWeekdays(group.days), locale, copy),
    hours: group.hours ?? copy.closed,
  }));
}

/** The hours of `date`'s weekday («12:00–23:00»), or null when that day is closed. */
export function todayHoursLabel(week: TWeekHours, date: Date): string | null {
  return dayHoursLabel(week[date.getDay()]);
}

/** Monday to Saturday 12:00–23:00, Sunday closed (the editor's start when there are no hours). */
export function defaultWeek(): TWeekHours {
  return Array.from({ length: DAYS_PER_WEEK }, (_, day) =>
    day === 0
      ? closedDay()
      : { open: true, ranges: [{ from: '12:00', to: '23:00' }] }
  );
}
